import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import _ from "lodash";
import CustomTable, { Column } from "@Components/Table";
import BlackPinTag from "@Components/BlackPinTag";
import ConfirmationModal from "@Components/ConfirmationModel/confirmation-model";
import { useAppSelector } from "@hooks";
import { RootState } from "@Store";
import { selectCurrentOrganization } from "@Store/Organizations/selectors";
import { usersListRequest } from "@Store/Users/actions";
import {
  approveUserUpdateRequest,
  declineUserUpdateRequest,
  userUpdateRequestsListRequest,
} from "@Store/UserUpdateRequests/actions";
import {
  selectUserUpdateRequests,
  selectUserUpdateRequestActionInFlight,
  selectUserUpdateRequestsLoading,
} from "@Store/UserUpdateRequests/selectors";
import { IOrgUserUpdateRequest } from "@Store/UserUpdateRequests/types";
import { IOrganizationUser } from "@Store/Organizations/types";
import { StyledOutlinedInput } from "@Pages/AdminPages/UsersManagement";

type PendingAction = "approve" | "decline";

interface PendingConfirmationState {
  action: PendingAction;
  request: IOrgUserUpdateRequest;
  userName: string;
  currentMobile: string;
}

const PendingUserRequestsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationState, setConfirmationState] =
    useState<PendingConfirmationState | null>(null);

  const {
    currentOrganization,
    requests,
    users,
    isLoading,
    actionInFlight,
  } = useAppSelector((state: RootState) => ({
    currentOrganization: selectCurrentOrganization(state),
    requests: selectUserUpdateRequests(state),
    users: state.users.general.list,
    isLoading: selectUserUpdateRequestsLoading(state),
    actionInFlight: selectUserUpdateRequestActionInFlight(state),
  }));

  useEffect(() => {
    if (currentOrganization?.orgId) {
      const orgId = currentOrganization.orgId;
      dispatch(userUpdateRequestsListRequest(orgId));
      dispatch(usersListRequest({ orgId }));
    }
  }, [currentOrganization, dispatch]);

  const usersById = useMemo(() => {
    const map = new Map<string, IOrganizationUser>();
    users.forEach((user) => map.set(user.userId, user));
    return map;
  }, [users]);

  const getUserDisplayName = (userId: string): string => {
    const user = usersById.get(userId);
    if (!user) return userId;
    const name = `${user.givenName ?? ""} ${user.surName ?? ""}`.trim();
    return name || userId;
  };

  const getCurrentMobile = (userId: string): string =>
    usersById.get(userId)?.mobileNumber ?? "—";

  const handleSearch = useCallback(
    _.debounce((query: string) => {
      setSearchQuery(query);
    }, 300),
    []
  );

  const openConfirmation = (
    action: PendingAction,
    request: IOrgUserUpdateRequest
  ) => {
    setConfirmationState({
      action,
      request,
      userName: getUserDisplayName(request.userId),
      currentMobile: getCurrentMobile(request.userId),
    });
    setConfirmationOpen(true);
  };

  const handleConfirm = () => {
    if (!confirmationState || !currentOrganization?.orgId) return;

    const payload = {
      orgId: currentOrganization.orgId,
      requestId: confirmationState.request.id,
    };

    if (confirmationState.action === "approve") {
      dispatch(approveUserUpdateRequest(payload));
    } else {
      dispatch(declineUserUpdateRequest(payload));
    }

    setConfirmationOpen(false);
    setConfirmationState(null);
  };

  const handleCancel = () => {
    setConfirmationOpen(false);
    setConfirmationState(null);
  };

  const getStatusView = (request: IOrgUserUpdateRequest) => {
    if (request.approvalTs) {
      return (
        <BlackPinTag
          color="Green"
          text={t("pendingUserRequests.status.approved")}
        />
      );
    }
    if (request.declineTs) {
      return (
        <BlackPinTag
          color="Red"
          text={t("pendingUserRequests.status.declined")}
        />
      );
    }
    return (
      <BlackPinTag
        color="Orange"
        text={t("pendingUserRequests.status.pending")}
      />
    );
  };

  const getActionsView = (request: IOrgUserUpdateRequest) => {
    if (request.approvalTs) {
      return t("pendingUserRequests.actions.approvedAt", {
        date: request.approvalTs.toLocaleString(),
      });
    }
    if (request.declineTs) {
      return t("pendingUserRequests.actions.declinedAt", {
        date: request.declineTs.toLocaleString(),
      });
    }

    const isActionDisabled = actionInFlight === request.id;

    return (
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={isActionDisabled}
          onClick={(e) => {
            e.stopPropagation();
            openConfirmation("approve", request);
          }}
        >
          {t("pendingUserRequests.buttons.approve")}
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          disabled={isActionDisabled}
          onClick={(e) => {
            e.stopPropagation();
            openConfirmation("decline", request);
          }}
        >
          {t("pendingUserRequests.buttons.decline")}
        </Button>
      </Box>
    );
  };

  const processedRequestIds = useMemo(
    () =>
      new Set(
        requests.filter((request) => request.processed).map((request) => request.id)
      ),
    [requests]
  );

  const columns: Column[] = [
    {
      field: "requestType",
      headerName: t("pendingUserRequests.columns.requestType"),
    },
    {
      field: "userName",
      headerName: t("pendingUserRequests.columns.userName"),
      sortable: true,
    },
    {
      field: "currentMobile",
      headerName: t("pendingUserRequests.columns.currentMobile"),
    },
    {
      field: "requestedMobile",
      headerName: t("pendingUserRequests.columns.requestedMobile"),
    },
    {
      field: "requestedAt",
      headerName: t("pendingUserRequests.columns.requestedAt"),
      sortable: true,
    },
    {
      field: "statusView",
      headerName: t("pendingUserRequests.columns.status"),
    },
    {
      field: "actionsView",
      headerName: t("pendingUserRequests.columns.actions"),
    },
  ];

  const data = requests
    .filter((request) => {
      const userName = getUserDisplayName(request.userId);
      const currentMobile = getCurrentMobile(request.userId);
      const haystack =
        `${userName} ${request.userId} ${currentMobile} ${request.newMobileNumber}`.toLowerCase();
      return haystack.includes(searchQuery.toLowerCase());
    })
    .map((request) => ({
      id: request.id,
      requestType: t("pendingUserRequests.requestType.phoneNumberChange"),
      userName: getUserDisplayName(request.userId),
      currentMobile: getCurrentMobile(request.userId),
      requestedMobile: request.newMobileNumber,
      requestedAt: request.requestTs.toLocaleString(),
      statusView: getStatusView(request),
      actionsView: getActionsView(request),
    }));

  const confirmationTitle =
    confirmationState?.action === "approve"
      ? t("pendingUserRequests.modals.confirmApproveTitle")
      : t("pendingUserRequests.modals.confirmDeclineTitle");

  const confirmationContent = confirmationState
    ? confirmationState.action === "approve"
      ? t("pendingUserRequests.modals.confirmApproveContent", {
          name: confirmationState.userName,
          current: confirmationState.currentMobile,
          requested: confirmationState.request.newMobileNumber,
        })
      : t("pendingUserRequests.modals.confirmDeclineContent", {
          name: confirmationState.userName,
          current: confirmationState.currentMobile,
          requested: confirmationState.request.newMobileNumber,
        })
    : "";

  return (
    <Box className="root">
      <div className="clients-organisation-header">
        <h2>{t("pendingUserRequests.title")}</h2>
      </div>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "end",
          marginBottom: "20px",
        }}
      >
        <StyledOutlinedInput
          id="outlined-adornment-search-pending-requests"
          type="text"
          placeholder={t("pendingUserRequests.searchPlaceholder")}
          startAdornment={
            <InputAdornment position="start">
              <IconButton aria-label="search icon" edge="start">
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          }
          onChange={(e) => handleSearch(e.target.value)}
          label="Search"
        />
      </div>
      <CustomTable
        columns={columns}
        data={data}
        rowsPerPage={10}
        selectable={false}
        isRowDisabled={(row) => processedRequestIds.has(row.id)}
      />
      {isLoading && data.length === 0 && (
        <Box sx={{ mt: 2, color: "text.secondary" }}>
          {t("pendingUserRequests.loading")}
        </Box>
      )}
      <ConfirmationModal
        key={
          confirmationOpen
            ? `${confirmationState?.action}-${confirmationState?.request.id}`
            : "closed"
        }
        open={confirmationOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={confirmationTitle}
        content={confirmationContent}
      />
    </Box>
  );
};

export default PendingUserRequestsPage;
